<template>
    <div class="input-row">
        <div class="field-state input-folder">
            <div class="input-folder--input">
                <PrimeInputText
                    :aria-required="true"
                    v-model="name" 
                    @input="emit('addressChanged')"
                    type="text" 
                    id="name" 
                    placeholder=" " 
                    required 
                    aria-label="Enter your name"
                    :readonly="!props.canEdit"
                    autocomplete="false"
                    maxlength="48"
                    :class="{'invalid-state-input': props.errors.name != null}"
                />
                <label for="name">
                    <p class="paragraph-default">Name *</p>
                </label>
            </div>
            <p class="input-support-text invalid-state">{{ props.errors.name }}</p>
        </div>
        <div class="field-state input-folder">
            <div class="input-folder--input">
                <PrimeInputText
                    v-model="inCareOf"
                    @input="emit('addressChanged')" 
                    type="text" 
                    id="in_care_of"
                    name="in_care_of"
                    placeholder=" " 
                    aria-label="Enter in care of information"
                    :readonly="!props.canEdit"
                    autocomplete="false"
                    maxlength="48"
                    :class="{'invalid-state-input': props.errors.in_care_of != null}"
                />
                <label for="in_care_of">
                    <p class="paragraph-default">In Care Of</p>
                </label>
            </div>
            <p class="input-support-text invalid-state">{{ props.errors.in_care_of }}</p>
        </div>
    </div>
    <div class="input-row">
        <div class="field-state input-folder">
            <div class="input-folder--input">
                <PrimeInputText
                    v-model="street1"
                    :aria-required="true"
                    @input="emit('addressChanged')" 
                    type="text" 
                    id="street1" 
                    placeholder=" " 
                    required 
                    aria-label="Enter your address"
                    :readonly="!props.canEdit"
                    autocomplete="false"
                    name="street1"
                    maxlength="48"
                    :class="{'invalid-state-input': props.errors.address != null}"
                />
                <label for="street1">
                    <p class="paragraph-default">Address *</p>
                </label>
            </div>
            <p class="input-support-text invalid-state">{{ props.errors.address }}</p>
        </div>
        <div class="field-state input-folder">
            <div class="input-folder--input">
                <PrimeInputText
                    v-model="street2"
                    @input="emit('addressChanged')" 
                    type="text" 
                    id="street2" 
                    placeholder=" " 
                    aria-label="Enter your address line 2"
                    :readonly="!props.canEdit"
                    autocomplete="false"
                    name="street2"
                    maxlength="48"
                    :class="{'invalid-state-input': props.errors.address_2 != null}"
                />
                <label for="street2">
                    <p class="paragraph-default">Address 2 (Suite, Unit, PO Box) (Optional)</p>
                </label>
            </div>
            <p class="input-support-text invalid-state">{{ props.errors.address_2 }}</p>
        </div>
    </div>
    <div class="input-row">
        <div class="field-state input-folder">
            <div class="input-folder--input">
                <PrimeInputText
                    v-model="city"
                    @input="emit('addressChanged')" 
                    type="text" 
                    id="city" 
                    placeholder=" " 
                    :required="requireStateCity" 
                    aria-label="Enter your city"
                    :aria-required="requireStateCity"
                    :readonly="!props.canEdit"
                    autocomplete="false"
                    name="city"
                    maxlength="30"
                    :class="{'invalid-state-input': props.errors.city != null}"
                />
                <label for="city">
                    <p class="paragraph-default">City {{ requireStateCity ? '*' : '' }}</p>
                </label>
            </div>
            <p class="input-support-text invalid-state">{{ props.errors.city }}</p>
        </div>
        <div class="field-state input-folder">
            <custom-select
                v-model="selectedRegion"
                :label="requireStateCity ? 'State / Province *' : 'Region'"
                name="state" 
                id="state" 
                :aria-required="requireStateCity"
                aria-label="Enter your state"
                :readonly="!props.canEdit"
                :options="formattedRegions"
                :error="props.errors.state != null"
            />
            <div class="field-state input-folder"><p class="input-support-text invalid-state">{{ props.errors.state }}</p></div>
        </div>
    </div>
    <div class="input-row">
        <div class="field-state input-folder">
            <div class="input-folder--input">
                <PrimeInputText
                    v-model="zip"
                    @input="emit('addressChanged')" 
                    :aria-required="requireStateCity"
                    type="text" 
                    id="zip" 
                    placeholder=" " 
                    :required="requireStateCity" 
                    aria-label="Enter your postal or ZIP code"
                    :readonly="!props.canEdit"
                    autocomplete="false"
                    name="zip"
                    maxlength="12"
                    :class="{'invalid-state-input': props.errors.zip != null}"
                />
                <label for="zip">
                    <p class="paragraph-default">Postal / ZIP Code {{ requireStateCity ? "*" : ""}}</p>
                </label>
            </div>
            <p class="input-support-text invalid-state">{{ props.errors.zip }}</p>
        </div>
        <div class="input-folder field-state">
        <!-- <div class="selectore-state select-wrapper state-input"> -->
            <custom-select
            v-model="selectedCountry"
            :aria-required="true"
            label="Country *"
            name="country" 
            id="country" 
            aria-label="Enter your country"
            :readonly="!props.canEdit"
            :options="formattedCountries"
            :error="props.errors.country != null"
            />
            <div class="field-state input-folder"><p class="input-support-text invalid-state">{{ props.errors.country }}</p></div>
        <!-- </div> -->
        </div>
    </div>
    <div class="input-row" v-if="validationEnabled">
        <p id="invalid-address-message" v-if="!isValid" class="paragraph-default invalid-state">
            The address you entered may be invalid. Invalid addresses may cause delays in shipping.
        </p>
        <p id="suggested-address-message" v-else-if="isValid && isDifferent" class="paragraph-default">
            <a class="text-link" @click="openSuggestedAddressModal">
                Use Suggested Address
            </a>
        </p>
    </div>
 
</template>
<script setup lang="ts">
    import type { PropType } from "vue";
    import { watch, computed, toRef, watchEffect } from "vue";
    import { useAddressValidation } from "@/helpers/composables/useAddressValidation";
    import SuggestedAddressModal from "@/components/SuggestedAddressModal.vue";
    import { useDialog } from "primevue/usedialog";

    const dialog = useDialog();
    interface Country {
        alpha2code: string;
        alpha3code: string;
        name: string;
    }

    interface Region {
        code: string;
        name: string;
    }

    const emit = defineEmits(["addressChanged"]);
    let name = defineModel("name", { default: "" });
    let inCareOf = defineModel("inCareOf", { default: "" });
    let street1 = defineModel("street1", { default: "" });
    let street2 = defineModel("street2", { default: "" });
    let city = defineModel("city", { default: "" });
    let state = defineModel("state", { default: "" });
    let zip = defineModel("zip", { default: "" });
    let country = defineModel("country", { default: "" });
    let addressValid = defineModel("addressValid", { default: false });

    let props = defineProps({
        canEdit: {
            type: Boolean,
            default: true
        },
        regions: {
            type: Array as PropType<Region[]>,
            default: () => []
        },
        countries: {
            type: Array as PropType<Country[]>,
            default: () => []
        },
        errors: {
            type: Object,
            default: () => {
                return {};
            }
        },
        enableValidation: {
            type: Boolean,
            default: false
        }
    });

    let isEnabled = toRef(props, "enableValidation");
    
    let openSuggestedAddressModal = () => {
        dialog.open(SuggestedAddressModal, {
            props: {
                showHeader: false,
            },
            data: {
                oldAddress: currentAddress.value,
                newAddress: suggestedAddress.value,
            },
            onClose: (props) => {
                if(props?.data?.useSuggestedAddress){
                    useSuggestedAddress();
                }
            }
        })
    };

    let { isValid, isDifferent, validationEnabled, validAddress, isLoadingValidation } =
        useAddressValidation(
            {
                street1,
                street2,
                city,
                state,
                postal_code: zip,
                country: country
            },
            isEnabled
        );

    let currentAddress = computed(() => {
        return {
            name: name.value,
            inCareOf: inCareOf.value,
            street1: street1.value,
            street2: street2.value,
            city: city.value,
            state: state.value,
            zip: zip.value,
            country: country.value
        };
    });

    let suggestedAddress = computed(() => {
        return {
            ...currentAddress.value,
            street1: validAddress.value.street1
                ? validAddress.value.street1
                : currentAddress.value.street1,
            street2: validAddress.value.street2
                ? validAddress.value.street2
                : currentAddress.value.street2,
            city: validAddress.value.city
                ? validAddress.value.city
                : currentAddress.value.city,
            state: validAddress.value.state
                ? validAddress.value.state
                : currentAddress.value.state,
            zip: validAddress.value.postal_code
                ? validAddress.value.postal_code
                : currentAddress.value.zip,
            country: validAddress.value.country
                ? validAddress.value.country
                : currentAddress.value.country
        };
    });

    let useSuggestedAddress = () => {
        street1.value = suggestedAddress.value.street1;
        street2.value = suggestedAddress.value.street2;
        city.value = suggestedAddress.value.city;
        state.value = suggestedAddress.value.state;
        zip.value = suggestedAddress.value.zip;
        country.value = suggestedAddress.value.country;
    };

    const CANADA_STATE_CODES = new Set(['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT']);
    let formattedRegions = computed(()=>{
        const regions = props?.regions ?? [];
        const countryRegionMap = {
            'CAN': regions
                .filter(region => CANADA_STATE_CODES.has(region.code) && region.code !== 'PR')
                .map(region => ({ key: region.code, value: region.name })),
            'PRI': [{ key: 'PR', value: 'Puerto Rico' }],
            'USA': regions
                .filter(region => !CANADA_STATE_CODES.has(region.code))
                .map(region => ({ key: region.code, value: region.name }))
        };
        return countryRegionMap[country.value] ?? [];
    })
    watch(() => country.value, (newCountryValue, oldCountryValue) => {
        if (!['USA', 'CAN', 'PRI'].includes(newCountryValue) && ['USA', 'CAN', 'PRI'].includes(oldCountryValue)) {
            state.value = '';
            return;
        }
        if (['USA', 'CAN', 'PRI'].includes(newCountryValue)) {
            let stateVal = state.value?.toLowerCase();
            let availableStates = formattedRegions.value || [];

            let matchedState = availableStates.find(
                s => s.value?.toLowerCase() === stateVal || s.key?.toLowerCase() === stateVal
            );
            state.value = matchedState ? matchedState.key : '';
        }
    });

    let formattedCountries = computed(() => {
        return props.countries.map((country) => {
            return {
                key: country.alpha3code,
                value: country.name
            };
        });
    });   
    
    let selectedRegion = computed({
        get(){
            return formattedRegions.value.find(region=>region.key == state.value);
        },
        set(value){
            state.value = value.key;
        }
    })

    let selectedCountry = computed({
        get(){
            return formattedCountries.value.find(cntry=>cntry.key == country.value);
        },
        set(value){
            country.value = value.key;
        }
    })

    const requireStateCity = computed(()=>{
        return !!country.value && ['US','USA','CA','CAN'].includes(country.value);
    })

    watchEffect(()=>{
        addressValid.value = (!isEnabled.value) || (isValid.value && !isLoadingValidation.value);
    })
</script>
